import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  marketplaceApi,
  type MarketplaceOrdersFilters,
  type MarketplaceReturnsFilters,
  type UpdateMarketplaceSettingsInput,
  type GenerateMarketplaceAwbInput,
} from '../services/marketplace-api';
import { marketplaceLabel } from '../platforms';

export function useMarketplaceOrders(platform: string, filters: MarketplaceOrdersFilters, enabled = true) {
  return useQuery({
    queryKey: ['marketplace', platform, 'orders', filters],
    queryFn: () => marketplaceApi.orders(platform, filters),
    enabled,
  });
}

export function useMarketplaceSettings(platform: string, enabled = true) {
  return useQuery({
    queryKey: ['marketplace', platform, 'settings'],
    queryFn: () => marketplaceApi.settings(platform),
    enabled,
  });
}

/** Cifra roșie din bara laterală; facturarea și sincronizarea o reîmprospătează imediat. */
export const UNINVOICED_COUNT_KEY = ['marketplace-uninvoiced-count'] as const;

export function useMarketplaceUninvoicedCounts(enabled: boolean) {
  return useQuery({
    queryKey: UNINVOICED_COUNT_KEY,
    queryFn: () => marketplaceApi.uninvoicedCount(),
    enabled,
    // comenzile sosesc prin sincronizarea de pe server (la 30 min); un minut ajunge
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useUpdateMarketplaceSettings(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMarketplaceSettingsInput) => marketplaceApi.updateSettings(platform, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
      toast.success('Setări salvate');
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la salvare'),
  });
}

export function useMarketplaceSync(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => marketplaceApi.sync(platform),
    onSuccess: (res) => {
      if (res.async) {
        toast.info('Sincronizarea continuă în fundal — comenzile apar în câteva minute.');
        // prima sincronizare poate dura: revalidam la 30s si 90s (tiparul sync email)
        const refresh = () => {
          qc.invalidateQueries({ queryKey: ['marketplace', platform] });
          qc.invalidateQueries({ queryKey: UNINVOICED_COUNT_KEY });
        };
        setTimeout(refresh, 30_000);
        setTimeout(refresh, 90_000);
      } else {
        const errs = res.errors?.length ? ` (${res.errors.length} erori)` : '';
        toast.success(`Sincronizat — ${res.orders ?? 0} comenzi${errs}`);
      }
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
      qc.invalidateQueries({ queryKey: UNINVOICED_COUNT_KEY });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la sincronizare'),
  });
}

export function useSyncMarketplaceStock(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => marketplaceApi.syncStock(platform),
    onSuccess: (res) => {
      const parts = [`${res.pushed} actualizate`];
      if (res.unresolved > 0) parts.push(`${res.unresolved} fără ofertă`);
      if (res.errors?.length) parts.push(`${res.errors.length} erori`);
      toast.success(`Stoc sincronizat — ${parts.join(', ')}`);
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la sincronizarea stocului'),
  });
}

export function useInvoiceOrder(platform: string) {
  const qc = useQueryClient();
  const label = marketplaceLabel(platform);
  return useMutation({
    mutationFn: (id: string) => marketplaceApi.invoiceOrder(id),
    onSuccess: (res) => {
      if (res.attached) toast.success(`Factura ${res.label} a fost emisă și atașată comenzii în ${label}`);
      else if (res.attachError) toast.warning(`Factura ${res.label} a fost emisă, dar atașarea în ${label} a eșuat — se reîncearcă automat`);
      else toast.success(`Factura ${res.label} a fost emisă`);
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la facturare'),
    // și la refuz: serverul poate lega între timp factura găsită „din alt sistem”
    onSettled: () => qc.invalidateQueries({ queryKey: UNINVOICED_COUNT_KEY }),
  });
}

export function useStornoOrder(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketplaceApi.stornoOrder(id),
    onSuccess: (res) => {
      toast.success(`Storno emis pentru comanda anulată — factura ${res.label}`);
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la emiterea storno-ului'),
  });
}

export function useTestMarketplaceConnection(platform: string) {
  return useMutation({ mutationFn: () => marketplaceApi.testConnection(platform) });
}

export function useSetProductMapping(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { externalCode: string; productId: string | null }) =>
      marketplaceApi.setProductMapping(platform, vars.externalCode, vars.productId),
    onSuccess: (_r, vars) => {
      toast.success(vars.productId ? 'Produs asociat' : 'Asocierea a fost ștearsă');
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la salvare'),
  });
}

export function useMarketplaceReturns(platform: string, filters: MarketplaceReturnsFilters) {
  return useQuery({
    queryKey: ['marketplace', platform, 'returns', filters],
    queryFn: () => marketplaceApi.returns(platform, filters),
  });
}

export function useStornoReturn(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketplaceApi.stornoReturn(id),
    onSuccess: (res) => {
      if (res.status === 'stornat') toast.success('Storno emis pentru retur');
      else toast.warning(res.message || 'Returul necesită verificare — vezi detaliile');
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la emiterea storno-ului'),
  });
}

/** Conturile de curier configurate la platformă (eMAG) — pt. dialogul AWB. */
export function useEmagCourierAccounts(platform: string, enabled = true) {
  return useQuery({
    queryKey: ['marketplace', platform, 'courier-accounts'],
    queryFn: () => marketplaceApi.courierAccounts(platform),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Generează AWB-ul comenzii LA platformă (eMAG creează AWB-ul → sincronizat nativ). */
export function useGenerateEmagAwb(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: GenerateMarketplaceAwbInput }) => marketplaceApi.generateAwb(vars.id, vars.input),
    onSuccess: (res) => {
      toast.success(`AWB ${res.awbNumber} generat în ${marketplaceLabel(platform)}`);
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la generarea AWB'),
  });
}

/** Reîmprospătează statusul AWB de la platformă (read-back manual). */
export function useRefreshEmagAwb(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shipmentId: string) => marketplaceApi.refreshAwb(shipmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketplace', platform] }),
    onError: (e: Error) => toast.error(e.message || 'Eroare la actualizarea statusului AWB'),
  });
}

/** Anulează AWB-ul (marcat în SF; eMAG cere anulare și în panou). */
export function useCancelEmagAwb(platform: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shipmentId: string) => marketplaceApi.cancelAwb(shipmentId),
    onSuccess: () => {
      // „Șterge AWB" doar marchează anulat în SF (ascunde chip-ul); anularea reală e la marketplace
      toast.info(`Pentru anularea AWB-ului trebuie accesată platforma ${marketplaceLabel(platform)}`);
      qc.invalidateQueries({ queryKey: ['marketplace', platform] });
    },
    onError: (e: Error) => toast.error(e.message || 'Eroare la ștergerea AWB'),
  });
}
